import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("tanakh_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .me()
      .then(setUser)
      .catch(() => {
        localStorage.removeItem("tanakh_token");
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const res = await api.login({ email, password });
    localStorage.setItem("tanakh_token", res.access_token);
    setUser(res.user);
    return res.user;
  };

  const register = async (email, password, name) => {
    const referral_code = localStorage.getItem("tanakh_referral_code") || undefined;
    const res = await api.register({ email, password, name, referral_code });
    localStorage.setItem("tanakh_token", res.access_token);
    setUser(res.user);
    return res.user;
  };

  const logout = () => {
    localStorage.removeItem("tanakh_token");
    setUser(null);
  };

  const completeOnboarding = async () => {
    try {
      await api.completeOnboarding();
    } catch (e) {
      // Non-fatal: still mark locally so the user isn't trapped on the
      // onboarding screen because of a transient network error.
    }
    setUser((u) => (u ? { ...u, onboarding_completed: true } : u));
  };

  // Persist the user's grammatical gender for second-person address
  // (Chavruta / future gendered features). Updates server first, then
  // local state. `gender` ∈ {"male", "female", null}.
  const updateGender = useCallback(async (gender) => {
    const res = await api.setUserGender(gender);
    setUser((u) => (u ? { ...u, gender: res.gender } : u));
    return res.gender;
  }, []);

  // Stable reference — must not be recreated on every render, because
  // <AuthCallback /> uses this function in its useEffect dep array. If
  // the reference changed (which happens whenever setUser() runs and
  // re-renders AuthProvider), the callback effect would re-fire after
  // we cleared the OAuth hash, reading an empty hash and falsely
  // toasting "ההתחברות נכשלה". setUser from useState is itself stable,
  // so an empty dep list is safe here.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loginWithSession = useCallback(async (sessionId) => {
    const referral_code =
      localStorage.getItem("tanakh_referral_code") || undefined;
    const res = await api.googleSession(sessionId, referral_code);
    localStorage.setItem("tanakh_token", res.access_token);
    setUser(res.user);
    // Consume the referral code so it isn't replayed for future signins
    // on the same device.
    if (referral_code) localStorage.removeItem("tanakh_referral_code");
    return res;
  }, []);

  // Custom Google OAuth — same shape as `loginWithSession`, but uses
  // the Authorization Code we got back directly from Google instead of
  // an Emergent-managed session id.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setSessionFromOAuth = useCallback(async ({ code, redirect_uri }) => {
    const referral_code =
      localStorage.getItem("tanakh_referral_code") || undefined;
    const res = await api.googleOAuthExchange({ code, redirect_uri, referral_code });
    localStorage.setItem("tanakh_token", res.access_token);
    setUser(res.user);
    if (referral_code) localStorage.removeItem("tanakh_referral_code");
    return res;
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, loginWithSession, setSessionFromOAuth, completeOnboarding, updateGender }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
